import { inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { PersonGroupsActions } from '@states/person-groups/person-groups.actions';
import { personGroupsFeature } from '@states/person-groups/person-groups.feature';
import { PersonActions } from '@states/person/person.actions';
import { SharedActions } from '@states/shared/shared.action-types';
import { PersonGroupsModel } from '@models/person-groups.model';
import { ConfirmDialogModel, ConfirmDialogService } from '@ui';
import { PersonGroupsService } from '../../services/person-groups.service';
import { UtilsService } from '../../edge/utils.service';
import { AppState } from '../app.state';
import { TranslateService } from '@ngx-translate/core';
import { catchError, exhaustMap, map, mergeMap, Observable, of, switchMap, withLatestFrom } from 'rxjs';
import { personFeature } from '@states/person/person.feature';
import { PersonModel } from '@models/person.model';
import { OrganizationSelectors } from '@states/organization/organization.selector-types';
import { PersonService } from '../../services/person.service';
import { AlertModel } from '@models/alert.model';

export const startLoadingPersonGroups$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(PersonGroupsActions.startLoadingPersonGroups),
      exhaustMap(() => [
        PersonGroupsActions.setPersonGroupsLoading({ personGroupsLoading: true }),
        PersonGroupsActions.loadPersonGroups(),
      ]),
    ),
  { functional: true },
);

export const loadPersonGroups$ = createEffect(
  (actions$ = inject(Actions), service = inject(PersonGroupsService)) =>
    actions$.pipe(
      ofType(PersonGroupsActions.loadPersonGroups),
      switchMap(() =>
        service.getPersonGroups().pipe(
          switchMap((personGroups) => [
            PersonGroupsActions.loadPersonGroupsSuccess({ personGroups }),
            PersonGroupsActions.setPersonGroupsLoading({ personGroupsLoading: false }),
          ]),
          catchError(() => [
            PersonGroupsActions.loadPersonGroupsFail(),
            PersonGroupsActions.setPersonGroupsLoading({ personGroupsLoading: false }),
          ]),
        ),
      ),
    ),
  { functional: true },
);

export const startLoadingPersonGroupDetails$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(PersonGroupsActions.startLoadingPersonGroupDetails),
      exhaustMap(({ groupId }) => [
        PersonGroupsActions.setPersonGroupDetailsLoading({ personGroupDetailsLoading: true }),
        PersonGroupsActions.setPersonGroupMembersLoading({ personGroupMembersLoading: true }),
        PersonGroupsActions.loadPersonGroupDetails({ groupId }),
      ]),
    ),
  { functional: true },
);

export const loadPersonGroupDetails$ = createEffect(
  (actions$ = inject(Actions), service = inject(PersonGroupsService)) =>
    actions$.pipe(
      ofType(PersonGroupsActions.loadPersonGroupDetails),
      switchMap(({ groupId }) =>
        service.getPersonGroupDetails(groupId).pipe(
          switchMap((personGroupDetails) => [
            PersonGroupsActions.loadPersonGroupDetailsSuccess({ personGroupDetails }),
            PersonGroupsActions.setPersonGroupDetailsLoading({ personGroupDetailsLoading: false }),
          ]),
          catchError(() => [
            PersonGroupsActions.loadPersonGroupDetailsFail(),
            PersonGroupsActions.setPersonGroupDetailsLoading({ personGroupDetailsLoading: false }),
          ]),
        ),
      ),
    ),
  { functional: true },
);

export const loadPersonGroupMembers$ = createEffect(
  (
    actions$ = inject(Actions),
    personGroupsService = inject(PersonGroupsService),
    utilsService = inject(UtilsService),
  ) =>
    actions$.pipe(
      ofType(PersonGroupsActions.loadPersonGroupDetailsSuccess),
      switchMap(({ personGroupDetails }) =>
        personGroupsService.getPersonGroupPersons(personGroupDetails._id).pipe(
          switchMap((members) => [PersonGroupsActions.loadPersonGroupMembersSuccess({ members })]),
          catchError((response) => [
            PersonGroupsActions.loadPersonGroupMembersFail(),
            PersonGroupsActions.setPersonGroupMembersLoading({ personGroupMembersLoading: false }),
            SharedActions.showMessage({ error: utilsService.errMessage(response) }),
          ]),
        ),
      ),
    ),
  { functional: true },
);

export const requestRemovePersonsFromGroup$ = createEffect(
  (
    actions$ = inject(Actions),
    confirm = inject(ConfirmDialogService),
    translate = inject(TranslateService),
  ) =>
    actions$.pipe(
      ofType(PersonGroupsActions.requestRemovePersonsFromGroup),
      switchMap(({ groupId, groupName, personIds, requestId }) => {
        const count = personIds.length;
        const titleKey =
          count === 1
            ? 'PERSON_GROUPS.DETAIL.REMOVE_MEMBERS_CONFIRM_TITLE_ONE'
            : 'PERSON_GROUPS.DETAIL.REMOVE_MEMBERS_CONFIRM_TITLE_OTHER';
        const msgKey =
          count === 1
            ? 'PERSON_GROUPS.DETAIL.REMOVE_MEMBERS_CONFIRM_MSG_ONE'
            : 'PERSON_GROUPS.DETAIL.REMOVE_MEMBERS_CONFIRM_MSG_OTHER';

        return confirm
          .open({
            type: ConfirmDialogModel.ConfirmDialogType.CONFIRM,
            title: translate.instant(titleKey),
            msg: translate.instant(msgKey, {
              count,
              groupName,
            }),
            confirm: translate.instant('COMMON.DELETE'),
            cancel: translate.instant('COMMON.CANCEL'),
            disableClose: true,
          })
          .pipe(
            switchMap((result) => {
              if (
                result.selection === ConfirmDialogModel.ConfirmResult.OK &&
                result.type === ConfirmDialogModel.ConfirmDialogType.CONFIRM
              ) {
                return of(
                  PersonGroupsActions.removePersonsFromGroup({
                    groupId,
                    personIds,
                    requestId,
                  }),
                );
              }
              return of(PersonGroupsActions.removePersonsFromGroupCancel({ requestId }));
            }),
          );
      }),
    ),
  { functional: true },
);

export const removePersonsFromGroup$ = createEffect(
  (
    actions$ = inject(Actions),
    service = inject(PersonGroupsService),
    utils = inject(UtilsService),
    translateService = inject(TranslateService),
  ) =>
    actions$.pipe(
      ofType(PersonGroupsActions.removePersonsFromGroup),
      mergeMap(({ groupId, personIds, requestId }) =>
        service.removePersonsFromGroup(groupId, personIds).pipe(
          switchMap((removedPersonIds) => [
            PersonGroupsActions.removePersonsFromGroupSuccess({
              groupId,
              removedPersonIds,
              requestId,
            }),
          ]),
          catchError((response: unknown) => {
            const conflictActions = AlertModel.buildEventsInUseConflictActions(response, 'GROUP_IN_USE_MODAL', translateService);
            return of(
              PersonGroupsActions.removePersonsFromGroupFail({ requestId, personIds }),
              ...(conflictActions ?? [
                SharedActions.showMessage({
                  error: utils.errMessage(response as HttpErrorResponse | Error),
                }),
              ]),
            );
          }),
        ),
      ),
    ),
  { functional: true },
);

export const startAddingPersonsToGroups$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(PersonGroupsActions.startAddingPersonsToGroups),
      exhaustMap(({ groupIds, savedPersonIds, unsavedPersonDtos }) => [
        PersonGroupsActions.setIsAddingPersonsToGroups({ isAddingPersonsToGroups: true }),
        PersonGroupsActions.addPersonsToGroups({
          groupIds,
          savedPersonIds,
          unsavedPersonDtos,
        }),
      ]),
    ),
  { functional: true },
);

export const addPersonsToGroups$ = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store<AppState>),
    service = inject(PersonGroupsService),
    personsService = inject(PersonService),
    utils = inject(UtilsService),
    translate = inject(TranslateService),
  ) =>
    actions$.pipe(
      ofType(PersonGroupsActions.addPersonsToGroups),
      withLatestFrom(store.select(personFeature.selectUnsavedPersonsToken)),
      switchMap(([{groupIds, savedPersonIds, unsavedPersonDtos}, token]) => {
        let preparedPayload$!: Observable<PersonGroupsModel.AddPersonsToGroupsPreparedPayload>;

        if (unsavedPersonDtos.length) {
          if (!token) {
            return of(
              PersonGroupsActions.addPersonsToGroupsFail(),
              PersonGroupsActions.setIsAddingPersonsToGroups({
                isAddingPersonsToGroups: false,
              }),
              SharedActions.showMessage({
                error: translate.instant('TOASTS.FAILED_TO_SAVE', {
                  entity: translate.instant('PEOPLE.UNSAVED').toLowerCase(),
                }),
              }),
            );
          }

          preparedPayload$ = personsService.bulkCreatePendingPersons({data: unsavedPersonDtos, token})
            .pipe(map(
              (createdResponses) => {
                const createdPendingPersons: PersonModel.SavedPerson[] =
                  createdResponses.map((person, index) => ({
                    personId: person.personId,
                    name: person.name,
                    bestImage: person.bestImage,
                    bestTracker: person.bestTracker,
                    trainingStatus: person.trainingStatus,
                    isHidden: false,
                  }));


                return {
                  groupIds,
                  savedPersonIds,
                  createdPendingPersons
                }
              })
            )
        } else {
          preparedPayload$ = of({groupIds, savedPersonIds, createdPendingPersons: []})
        }

        return preparedPayload$.pipe(
          switchMap(({groupIds, savedPersonIds, createdPendingPersons}: PersonGroupsModel.AddPersonsToGroupsPreparedPayload) => {
              const pendingPersonsIds = createdPendingPersons.map((p) => p.personId);

              return service.addPersonsToGroups({
                groupIds,
                savedPersonIds: [...savedPersonIds, ...pendingPersonsIds],
              })
                .pipe(
                  withLatestFrom(store.select(personFeature.selectSavedPersonEntities)),
                  switchMap(([response, savedPersonEntities]) => {
                    const addedPersons = response.affectedPersonIds
                      .map((personId) => savedPersonEntities[personId])
                      .filter((person): person is PersonModel.SavedPerson => Boolean(person))
                      .concat(createdPendingPersons);

                    return [
                      PersonGroupsActions.addPersonsToGroupsSuccess({
                        groupIds,
                        addedPersons,
                      }),
                      PersonActions.bulkCreatePendingPersonsSuccess({
                        bucketIds: unsavedPersonDtos.map((item) => item.bucketId),
                        persons: createdPendingPersons,
                      }),
                      PersonActions.upsertSavedPersons({persons: addedPersons}),
                      PersonGroupsActions.setIsAddingPersonsToGroups({isAddingPersonsToGroups: false}),
                      SharedActions.showMessage({success: translate.instant('TOASTS.PERSONS_ADDED_TO_GROUPS_SUCCESS')}),
                    ];
                  }),
                  catchError((response: unknown) => [
                    PersonGroupsActions.addPersonsToGroupsFail(),
                    PersonGroupsActions.setIsAddingPersonsToGroups({isAddingPersonsToGroups: false}),
                    SharedActions.showMessage({
                      error: utils.errMessage(response as HttpErrorResponse | Error),
                    }),
                  ]),
                )
            }
          ),
          catchError((response: unknown) => [
            PersonGroupsActions.addPersonsToGroupsFail(),
            PersonGroupsActions.setIsAddingPersonsToGroups({ isAddingPersonsToGroups: false }),
            SharedActions.showMessage({
              error: utils.errMessage(response as HttpErrorResponse | Error),
            }),
          ]),
        )
      }),
    ),
  {functional: true},
);

export const joinPersonGroup$ = createEffect(
  (
    actions$ = inject(Actions),
    service = inject(PersonGroupsService),
    utils = inject(UtilsService),
    translate = inject(TranslateService),
  ) =>
    actions$.pipe(
      ofType(PersonGroupsActions.joinPersonGroup),
      switchMap(({ code }) =>
        service.joinPersonGroup(code).pipe(
          switchMap((personGroup) => [
            PersonGroupsActions.joinPersonGroupSuccess({ personGroup }),
            SharedActions.showMessage({
              success: translate.instant('TOASTS.PERSON_GROUP_JOIN_SUCCESS'),
            }),
          ]),
          catchError((response: unknown) =>
            of(
              PersonGroupsActions.joinPersonGroupFail(),
              SharedActions.showMessage({
                error: utils.errMessage(response as HttpErrorResponse | Error),
              }),
            ),
          ),
        ),
      ),
    ),
  { functional: true },
);

export const createPersonGroup$ = createEffect(
  (
    actions$ = inject(Actions),
    service = inject(PersonGroupsService),
    utils = inject(UtilsService),
    translate = inject(TranslateService),
  ) =>
    actions$.pipe(
      ofType(PersonGroupsActions.createPersonGroup),
      switchMap(({ name }) =>
        service.createPersonGroup(name).pipe(
          switchMap((personGroup) => [
            PersonGroupsActions.createPersonGroupSuccess({ personGroup }),
            SharedActions.showMessage({
              success: translate.instant('TOASTS.PERSON_GROUP_CREATE_SUCCESS'),
            }),
          ]),
          catchError((response: unknown) =>
            of(
              PersonGroupsActions.createPersonGroupFail(),
              SharedActions.showMessage({
                error: utils.errMessage(response as HttpErrorResponse | Error),
              }),
            ),
          ),
        ),
      ),
    ),
  { functional: true },
);

export const updatePersonGroup$ = createEffect(
  (actions$ = inject(Actions), service = inject(PersonGroupsService)) =>
    actions$.pipe(
      ofType(PersonGroupsActions.updatePersonGroup),
      switchMap(({ id, name }) =>
        service.updatePersonGroup(id, name).pipe(
          switchMap((personGroup) => [
            PersonGroupsActions.updatePersonGroupSuccess({ personGroup }),
          ]),
          catchError(() => of(PersonGroupsActions.updatePersonGroupFail())),
        ),
      ),
    ),
  { functional: true },
);

export const requestDeletePersonGroup$ = createEffect(
  (
    actions$ = inject(Actions),
    confirm = inject(ConfirmDialogService),
    translate = inject(TranslateService),
  ) =>
    actions$.pipe(
      ofType(PersonGroupsActions.requestDeletePersonGroup),
      switchMap(({ id, name }) =>
        confirm
          .open({
            type: ConfirmDialogModel.ConfirmDialogType.CONFIRM,
            title: translate.instant('PERSON_GROUPS.DELETE_DIALOG.TITLE'),
            msg: translate.instant('PERSON_GROUPS.DELETE_DIALOG.MESSAGE', { name: `"${name}"` }),
            confirm: translate.instant('COMMON.DELETE'),
            cancel: translate.instant('COMMON.CANCEL'),
            disableClose: true,
          })
          .pipe(
            switchMap((result) => {
              if (
                result.selection === ConfirmDialogModel.ConfirmResult.OK &&
                result.type === ConfirmDialogModel.ConfirmDialogType.CONFIRM
              ) {
                return [
                  PersonGroupsActions.addPersonGroupToProcessingIDs({ id }),
                  PersonGroupsActions.deletePersonGroup({ id }),
                ];
              }
              return of(SharedActions.doNothing());
            }),
          ),
      ),
    ),
  { functional: true },
);

export const deletePersonGroup$ = createEffect(
  (
    actions$ = inject(Actions),
    service = inject(PersonGroupsService),
    utils = inject(UtilsService),
    translateService = inject(TranslateService),
  ) =>
    actions$.pipe(
      ofType(PersonGroupsActions.deletePersonGroup),
      switchMap(({ id }) =>
        service.deletePersonGroup(id).pipe(
          switchMap(() => [
            PersonGroupsActions.deletePersonGroupSuccess({ id }),
            PersonGroupsActions.removePersonGroupFromProcessingIDs({ id }),
          ]),
          catchError((response: unknown) => {
            const conflictActions = AlertModel.buildEventsInUseConflictActions(response, 'GROUP_IN_USE_MODAL', translateService);
            return [
              PersonGroupsActions.deletePersonGroupFail(),
              PersonGroupsActions.removePersonGroupFromProcessingIDs({ id }),
              ...(conflictActions ?? [
                SharedActions.showMessage({
                  error: utils.errMessage(response as HttpErrorResponse | Error),
                }),
              ]),
            ];
          }),
        ),
      ),
    ),
  { functional: true },
);

export const startLoadingPersonGroupShareOptions$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(PersonGroupsActions.startLoadingPersonGroupShareOptions),
      exhaustMap(({ groupId }) => [PersonGroupsActions.loadPersonGroupAccessList({ groupId })]),
    ),
  { functional: true },
);

export const loadPersonGroupAccessList$ = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store<AppState>),
    service = inject(PersonGroupsService),
    utils = inject(UtilsService),
  ) =>
    actions$.pipe(
      ofType(PersonGroupsActions.loadPersonGroupAccessList),
      withLatestFrom(store.select(OrganizationSelectors.selectOrgId)),
      switchMap(([{ groupId }, activeOrgId]) =>
        service.loadPersonGroupAccessList(groupId).pipe(
          switchMap((response) => {
            const afterAccessList = [
              PersonGroupsActions.loadPersonGroupAccessListSuccess({
                groupId: response.groupId,
                createdByOrgId: response.createdByOrgId,
                personGroupAccessList: response.accessList,
              }),
              PersonGroupsActions.setPersonGroupAccessListLoading({ personGroupAccessListLoading: false }),
            ];
            const myRole = PersonGroupsModel.resolveMyShareAccessRoleFromSharedWithRows(
              response.accessList,
              response.createdByOrgId,
              activeOrgId,
            );
            const invitable = PersonGroupsModel.invitableShareRolesForCaller(myRole);
            if (invitable.length > 0) {
              return [
                ...afterAccessList,
                PersonGroupsActions.loadPersonGroupInviteCode({
                  groupId: response.groupId,
                  accessType: invitable[0],
                }),
              ];
            }
            return [
              ...afterAccessList,
              PersonGroupsActions.setPersonGroupInviteCodeLoading({ personGroupInviteCodeLoading: false }),
            ];
          }),
          catchError((response: unknown) => [
            PersonGroupsActions.loadPersonGroupAccessListFail(),
            PersonGroupsActions.setPersonGroupAccessListLoading({ personGroupAccessListLoading: false }),
            PersonGroupsActions.setPersonGroupInviteCodeLoading({ personGroupInviteCodeLoading: false }),
            SharedActions.showMessage({
              error: utils.errMessage(response as HttpErrorResponse | Error),
            }),
          ]),
        ),
      ),
    ),
  { functional: true },
);

export const loadPersonGroupInviteCode$ = createEffect(
  (
    actions$ = inject(Actions),
    service = inject(PersonGroupsService),
    utils = inject(UtilsService),
  ) =>
    actions$.pipe(
      ofType(PersonGroupsActions.loadPersonGroupInviteCode),
      switchMap(({ groupId, accessType }) =>
        service.loadPersonGroupInviteCode(groupId, accessType).pipe(
          switchMap((personGroupInviteCode) => [
            PersonGroupsActions.loadPersonGroupInviteCodeSuccess({
              groupId,
              personGroupInviteCode,
            }),
            PersonGroupsActions.setPersonGroupInviteCodeLoading({ personGroupInviteCodeLoading: false }),
          ]),
          catchError((response: unknown) => [
            PersonGroupsActions.loadPersonGroupInviteCodeFail(),
            PersonGroupsActions.setPersonGroupInviteCodeLoading({ personGroupInviteCodeLoading: false }),
            SharedActions.showMessage({
              error: utils.errMessage(response as HttpErrorResponse | Error),
            }),
          ]),
        ),
      ),
    ),
  { functional: true },
);

export const updatePersonGroupAccessListItem$ = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store<AppState>),
    service = inject(PersonGroupsService),
    utils = inject(UtilsService),
    translate = inject(TranslateService),
  ) =>
    actions$.pipe(
      ofType(PersonGroupsActions.updatePersonGroupAccessListItem),
      withLatestFrom(store.select(personGroupsFeature.selectShareOptions)),
      switchMap(([{ groupId, orgId, accessType }, shareOptions]) => {
        const previous = shareOptions.accessList.find((row) => row.orgId === orgId);
        if (!previous) {
          return of(
            PersonGroupsActions.updatePersonGroupAccessListItemFail({ orgId }),
            SharedActions.showMessage({
              error: translate.instant('TOASTS.PERSON_GROUP_SHARE_ACCESS_ROLE_UPDATE_NOT_FOUND'),
            }),
          );
        }
        return service.updatePersonGroupAccessListItem(groupId, orgId, accessType, previous).pipe(
          switchMap((personGroupSharedWithRow) => [
            PersonGroupsActions.updatePersonGroupAccessListItemSuccess({ personGroupSharedWithRow }),
            SharedActions.showMessage({
              success: translate.instant('TOASTS.PERSON_GROUP_SHARE_ACCESS_ROLE_UPDATED_SUCCESS'),
            }),
          ]),
          catchError((response: unknown) =>
            of(
              PersonGroupsActions.updatePersonGroupAccessListItemFail({ orgId }),
              SharedActions.showMessage({
                error: utils.errMessage(response as HttpErrorResponse | Error),
              }),
            ),
          ),
        );
      }),
    ),
  { functional: true },
);

export const removePersonGroupAccessListItem$ = createEffect(
  (
    actions$ = inject(Actions),
    service = inject(PersonGroupsService),
    utils = inject(UtilsService),
  ) =>
    actions$.pipe(
      ofType(PersonGroupsActions.removePersonGroupAccess),
      switchMap(({ groupId, orgId }) =>
        service.removePersonGroupAccessListItem(groupId, orgId).pipe(
          switchMap(() => [PersonGroupsActions.removePersonGroupAccessSuccess({ orgId })]),
          catchError((response: unknown) =>
            of(
              PersonGroupsActions.removePersonGroupAccessFail(),
              SharedActions.showMessage({
                error: utils.errMessage(response as HttpErrorResponse | Error),
              }),
            ),
          ),
        ),
      ),
    ),
  { functional: true },
);

export const requestRemovePersonGroupAccess$ = createEffect(
  (
    actions$ = inject(Actions),
    confirm = inject(ConfirmDialogService),
    translate = inject(TranslateService),
  ) =>
    actions$.pipe(
      ofType(PersonGroupsActions.requestRemovePersonGroupAccess),
      switchMap(({ groupId, orgId, orgName }) =>
        confirm
          .open({
            type: ConfirmDialogModel.ConfirmDialogType.CONFIRM,
            title: translate.instant('PERSON_GROUPS.SHARE_DIALOG.REMOVE_CONFIRM_TITLE', { orgName }),
            msg: translate.instant('PERSON_GROUPS.SHARE_DIALOG.REMOVE_CONFIRM_MSG', { orgName }),
            confirm: translate.instant('COMMON.DELETE'),
            cancel: translate.instant('COMMON.CANCEL'),
            disableClose: true,
            params: {
              type: PersonGroupsModel.ConfirmAction.RemovePersonGroupShareOrg,
              groupId,
              orgId,
            },
          })
          .pipe(
            switchMap((result) => {
              if (
                result.selection === ConfirmDialogModel.ConfirmResult.OK &&
                result.type === ConfirmDialogModel.ConfirmDialogType.CONFIRM
              ) {
                return of(
                  PersonGroupsActions.removePersonGroupAccess({
                    groupId,
                    orgId,
                  }),
                );
              }
              return of(SharedActions.doNothing());
            }),
          ),
      ),
    ),
  { functional: true },
);
